/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/require-default-props */
import { Flip } from '@gfazioli/mantine-flip';
import {
  ActionIcon,
  Badge,
  Group,
  Table as MantineTable,
  Paper,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconBackhoe,
  IconBriefcase,
  IconCheck,
  IconEdit,
  IconEye,
  IconPackage,
  IconPlus,
  IconRecycle,
  IconSchema,
  IconShare,
  IconTableRow,
  IconTarget,
  IconTruck,
} from '@tabler/icons-react';
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { hasCompletePlanDefinition } from '../../../../business/proposals/residue-plan';
import { useProposalResidueContext } from '../../../../contexts/core/proposals/ProposalResidue.context';
import { EntityTypeType, UnitOfMeasureType } from '../../../../models/core/cache.type';
import { Action } from '../../../../models/core/core.type';
import {
  ProposalResiduePlanCodeEnum,
  ProposalResiduePlanDestinationType,
  ProposalResiduePlanEquipmentType,
  ProposalResiduePlanPackagingType,
  ProposalResiduePlanQuotationDestinationType,
  ProposalResiduePlanQuotationEquipmentType,
  ProposalResiduePlanQuotationPackagingType,
  ProposalResiduePlanQuotationTreatmentType,
  ProposalResiduePlanQuotationType,
  ProposalResiduePlanQuotationVehicleType,
  ProposalResiduePlanTreatmentType,
  ProposalResiduePlanType,
  ProposalResiduePlanVehicleType,
  ProposalResidueType,
  ProposalServiceType,
} from '../../../../models/core/proposals.type';
import { formatCurrency } from '../../../../utils/formatter.utils';
import DeleteConfirmPopover from '../../../../components/DeleteConfirmPopover';
import ResiduePlanDestinationModal from './ResiduePlanDestinationModal';
import ResiduePlanEquipmentModal from './ResiduePlanEquipmentModal';
import ResiduePlanPackagingModal from './ResiduePlanPackagingModal';
import ResiduePlanTreatmentModal from './ResiduePlanTreatmentModal';
import ResiduePlanVehicleModal from './ResiduePlanVehicleModal';
import ResidueQuotationSummary from './ResidueQuotationSummary';

type ResiduePlanSummaryProps = {
  data: ProposalResidueType;
  isEdit?: boolean;
  requireSupplier?: boolean;
  planDisplayNumberFallback?: string | number | null;
  headerRightContent?: ReactNode;
  hideHeader?: boolean;
  hidePackagingSection?: boolean;
  hideEquipmentSection?: boolean;
  hideVehicleSection?: boolean;
  hideTreatmentSection?: boolean;
  hideDestinationSection?: boolean;
  forcePackagingSection?: boolean;
  forceEquipmentSection?: boolean;
  forceVehicleSection?: boolean;
  forceTreatmentSection?: boolean;
  forceDestinationSection?: boolean;
  disableAddPackagingAction?: boolean;
  disableAddEquipmentAction?: boolean;
  hideSectionSelectors?: boolean;
  defaultShowPackagingSection?: boolean;
  defaultShowEquipmentSection?: boolean;
  defaultShowVehicleSection?: boolean;
  defaultShowTreatmentSection?: boolean;
  defaultShowDestinationSection?: boolean;
  referenceData?: {
    entityTypeData?: EntityTypeType[];
    unitOfMeasures: UnitOfMeasureType[];
  };
  services?: ProposalServiceType[];
  zIndex?: number;
  onAddPlan?: (codigoPlanoTipo: ProposalResiduePlanCodeEnum) => void;
  onAddPlans?: (codigoPlanoTipos: ProposalResiduePlanCodeEnum[]) => void;
  onSetPrimaryPlan?: (plan: ProposalResiduePlanType) => void;
  onDeletePlan?: (plan: ProposalResiduePlanType) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  onPlanAction?: (plan: ProposalResiduePlanType, planTab?: string) => void;
  onPlanItemAction?: (
    plan: ProposalResiduePlanType,
    planTab: string,
    itemId?: string | number | null,
    itemAction?: 'add' | 'open'
  ) => void;
  onPlanItemDelete?: (plan: ProposalResiduePlanType, planTab: string, itemId: string | number) => void;
  onQuotationAction?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType | null
  ) => void;
  onQuotationToleranceAction?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType
  ) => void;
  onQuotationItemAction?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType,
    quotationTab: string,
    itemId: string | number
  ) => void;
  onQuotationItemToleranceAction?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType,
    quotationTab: string,
    itemId: string | number
  ) => void;
  onQuotationItemDelete?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType,
    quotationTab: string,
    itemId: string | number
  ) => void;
  onAddQuotationItem?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType,
    quotationTab: string
  ) => void;
  onAddQuotation?: (plan: ProposalResiduePlanType) => void;
  onSetPrimaryQuotation?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType
  ) => void;
  onDeleteQuotation?: (plan: ProposalResiduePlanType, quotation: ProposalResiduePlanQuotationType) => void;
  onEquipmentSubmit?: (
    plan: ProposalResiduePlanType,
    item: ProposalResiduePlanEquipmentType | null,
    action: string
  ) => void;
  onPackagingSubmit?: (
    plan: ProposalResiduePlanType,
    item: ProposalResiduePlanPackagingType | null,
    action: string
  ) => void;
  onVehicleSubmit?: (
    plan: ProposalResiduePlanType,
    item: ProposalResiduePlanVehicleType | null,
    action: string
  ) => void;
  onTreatmentSubmit?: (
    plan: ProposalResiduePlanType,
    item: ProposalResiduePlanTreatmentType | null,
    action: string
  ) => void;
  onDestinationSubmit?: (
    plan: ProposalResiduePlanType,
    item: ProposalResiduePlanDestinationType | null,
    action: string
  ) => void;
  onEquipmentQuotationSubmit?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType,
    refItem: ProposalResiduePlanEquipmentType,
    item: ProposalResiduePlanQuotationEquipmentType | null,
    action: string
  ) => void;
  onPackagingQuotationSubmit?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType,
    refItem: ProposalResiduePlanPackagingType,
    item: ProposalResiduePlanQuotationPackagingType | null,
    action: string
  ) => void;
  onVehicleQuotationSubmit?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType,
    refItem: ProposalResiduePlanVehicleType,
    item: ProposalResiduePlanQuotationVehicleType | null,
    action: string
  ) => void;
  onTreatmentQuotationSubmit?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType,
    refItem: ProposalResiduePlanTreatmentType,
    item: ProposalResiduePlanQuotationTreatmentType | null,
    action: string
  ) => void;
  onDestinationQuotationSubmit?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType,
    refItem: ProposalResiduePlanDestinationType,
    item: ProposalResiduePlanQuotationDestinationType | null,
    action: string
  ) => void;
};

type ResiduePlanFlipCardProps = {
  background: string;
  borderColor?: string;
  header: ReactNode;
  actions?: ReactNode;
  frontContent: ReactNode;
  backContent: ReactNode;
};

function ResiduePlanFlipCard(props: ResiduePlanFlipCardProps) {
  const [flipped, setFlipped] = useState<boolean>(false);
  const [height, setHeight] = useState(320);
  const frontRef = useRef<HTMLDivElement | null>(null);
  const backRef = useRef<HTMLDivElement | null>(null);

  const updateHeight = () => {
    const nextHeight = Math.max(frontRef.current?.scrollHeight || 0, backRef.current?.scrollHeight || 0);

    if (nextHeight > 0) {
      setHeight(nextHeight);
    }
  };

  useLayoutEffect(() => {
    updateHeight();
  });

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => updateHeight());

    if (frontRef.current) {
      observer.observe(frontRef.current);
    }

    if (backRef.current) {
      observer.observe(backRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Paper
      p="md"
      withBorder
      radius="lg"
      style={{
        width: '100%',
        background: props.background,
        borderColor: props.borderColor,
      }}
    >
      <Stack gap="md">
        <Group
          justify="space-between"
          align="flex-start"
          wrap="nowrap"
          onDoubleClick={() => setFlipped((current) => !current)}
        >
          {props.header}

          <Group gap={4}>
            {props.actions}
            <Tooltip label={flipped ? 'Voltar para itens do plano' : 'Resumo'} withArrow>
              <ActionIcon
                size="sm"
                variant="light"
                color={flipped ? 'grape' : 'blue'}
                onClick={() => setFlipped((current) => !current)}
              >
                {flipped ? <IconSchema size={15} /> : <IconTableRow size={15} />}
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        <Flip flipped={flipped} onChange={setFlipped} h={height} w="100%" perspective={5000} duration={0.25}>
          <Flip.Front>
            <div ref={frontRef}>{props.frontContent}</div>
          </Flip.Front>

          <Flip.Back>
            <div ref={backRef}>{props.backContent}</div>
          </Flip.Back>
        </Flip>
      </Stack>
    </Paper>
  );
}

export default function ResiduePlanSummary(props: ResiduePlanSummaryProps) {
  const { proposalResidueData } = useProposalResidueContext();
  const isAddPlanBaseDisabled: boolean = proposalResidueData
    ? !proposalResidueData.residuo ||
      !proposalResidueData.residuoCliente ||
      !proposalResidueData.codigoResiduoEstadoFisico ||
      !proposalResidueData.idEntidadeEndereco
    : !props.data.idResiduo ||
      !props.data.residuoCliente ||
      !props.data.codigoResiduoEstadoFisico ||
      !props.data.idEntidadeEndereco;

  const visiblePlans = [...props.data.planos].filter((x) => (x.action || Action.Nothing) !== Action.Delete);
  const getPlanKey = (plan: ProposalResiduePlanType) =>
    plan.id || plan.idPropostaResiduoPlano?.toString() || plan.idTemporaria || '';
  const getPlanDisplayNumber = (plan: ProposalResiduePlanType, index: number) =>
    plan.idPropostaResiduoPlano > -1
      ? plan.idPropostaResiduoPlano
      : props.planDisplayNumberFallback || index + 1 || -1;
  const ActionIconComponent = props.isEdit ? IconEdit : IconEye;
  // const planActionLabel = props.isEdit ? 'Editar plano' : 'Ver plano';
  // const quotationActionLabel = props.isEdit ? 'Editar Cotações' : 'Ver Cotações';
  const itemActionLabel = props.isEdit ? 'Editar' : 'Ver';
  const treatmentTabKey = props.isEdit ? 'Tratamento' : 'Tratamentos';
  const [selectedEquipmentPlan, setSelectedEquipmentPlan] = useState<ProposalResiduePlanType | null>(null);
  const [selectedEquipmentItem, setSelectedEquipmentItem] = useState<ProposalResiduePlanEquipmentType | null>(
    null
  );
  const [equipmentModalOpened, setEquipmentModalOpened] = useState(false);
  const [selectedPackagingPlan, setSelectedPackagingPlan] = useState<ProposalResiduePlanType | null>(null);
  const [selectedPackagingItem, setSelectedPackagingItem] = useState<ProposalResiduePlanPackagingType | null>(
    null
  );
  const [packagingModalOpened, setPackagingModalOpened] = useState(false);
  const [selectedVehiclePlan, setSelectedVehiclePlan] = useState<ProposalResiduePlanType | null>(null);
  const [selectedVehicleItem, setSelectedVehicleItem] = useState<ProposalResiduePlanVehicleType | null>(null);
  const [vehicleModalOpened, setVehicleModalOpened] = useState(false);
  const [selectedTreatmentPlan, setSelectedTreatmentPlan] = useState<ProposalResiduePlanType | null>(null);
  const [selectedTreatmentItem, setSelectedTreatmentItem] = useState<ProposalResiduePlanTreatmentType | null>(
    null
  );
  const [treatmentModalOpened, setTreatmentModalOpened] = useState(false);
  const [selectedDestinationPlan, setSelectedDestinationPlan] = useState<ProposalResiduePlanType | null>(
    null
  );
  const [selectedDestinationItem, setSelectedDestinationItem] =
    useState<ProposalResiduePlanDestinationType | null>(null);
  const [destinationModalOpened, setDestinationModalOpened] = useState(false);
  // const [planTypeModalOpened, setPlanTypeModalOpened] = useState(false);
  const [showPackagingSection, setShowPackagingSection] = useState<boolean>(
    props.defaultShowPackagingSection ?? true
  );
  const [showEquipmentSection, setShowEquipmentSection] = useState<boolean>(
    props.defaultShowEquipmentSection ?? false
  );
  const [showVehicleSection, setShowVehicleSection] = useState<boolean>(
    props.defaultShowVehicleSection ?? true
  );
  const [showTreatmentSection, setShowTreatmentSection] = useState<boolean>(
    props.defaultShowTreatmentSection ?? false
  );
  const [showDestinationSection, setShowDestinationSection] = useState<boolean>(
    props.defaultShowDestinationSection ?? true
  );

  const packagingSectionEnabled = props.forcePackagingSection ? true : showPackagingSection;
  const equipmentSectionEnabled = props.forceEquipmentSection ? true : showEquipmentSection;
  const vehicleSectionEnabled = props.forceVehicleSection ? true : showVehicleSection;
  const treatmentSectionEnabled = props.forceTreatmentSection ? true : showTreatmentSection;
  const destinationSectionEnabled = props.forceDestinationSection ? true : showDestinationSection;

  const isQuotationEffectivelyComplete = (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType
  ) => [quotation].length > 0 && hasCompletePlanDefinition(plan);

  const getPlanCode = (plan: ProposalResiduePlanType) =>
    plan.codigoPlanoTipo || ProposalResiduePlanCodeEnum.Mobilization;

  const isMobilizationPlan = (plan: ProposalResiduePlanType) =>
    getPlanCode(plan) === ProposalResiduePlanCodeEnum.Mobilization;

  const hasMobilizationPlan = visiblePlans.some((plan) => isMobilizationPlan(plan));
  const hasMobilizationSwitches =
    !props.hidePackagingSection && (packagingSectionEnabled || equipmentSectionEnabled);
  const hasRegularSwitches =
    (!props.hideVehicleSection && vehicleSectionEnabled) ||
    (!props.hideTreatmentSection && treatmentSectionEnabled) ||
    (!props.hideDestinationSection && destinationSectionEnabled);
  const isAddPlanDisabled =
    isAddPlanBaseDisabled ||
    (!hasMobilizationSwitches && !hasRegularSwitches) ||
    (hasMobilizationPlan && hasMobilizationSwitches && !hasRegularSwitches);

  useEffect(() => {
    const nextShowPackagingSection = visiblePlans.some((plan) =>
      plan.acondicionamentos.some((item) => (item.action || Action.Nothing) !== Action.Delete)
    );
    const nextShowEquipmentSection = visiblePlans.some((plan) =>
      plan.equipamentos.some((item) => (item.action || Action.Nothing) !== Action.Delete)
    );
    const nextShowVehicleSection = visiblePlans.some(
      (plan) => !!plan.veiculo && (plan.veiculo.action || Action.Nothing) !== Action.Delete
    );
    const nextShowTreatmentSection = visiblePlans.some(
      (plan) => !!plan.tratamento && (plan.tratamento.action || Action.Nothing) !== Action.Delete
    );
    const nextShowDestinationSection = visiblePlans.some(
      (plan) => !!plan.destinoFinal && (plan.destinoFinal.action || Action.Nothing) !== Action.Delete
    );

    setShowPackagingSection((current) => current || nextShowPackagingSection);
    setShowEquipmentSection((current) => current || nextShowEquipmentSection);
    setShowVehicleSection((current) =>
      props.hideVehicleSection ? false : current || nextShowVehicleSection
    );
    setShowTreatmentSection((current) =>
      props.hideTreatmentSection ? false : current || nextShowTreatmentSection
    );
    setShowDestinationSection((current) =>
      props.hideDestinationSection ? false : current || nextShowDestinationSection
    );
  }, [props.hideDestinationSection, props.hideTreatmentSection, props.hideVehicleSection, visiblePlans]);

  useEffect(() => {
    if (props.forcePackagingSection) {
      setShowPackagingSection(true);
    }
    if (props.forceEquipmentSection) {
      setShowEquipmentSection(true);
    }
    if (props.forceVehicleSection) {
      setShowVehicleSection(true);
    }
    if (props.forceTreatmentSection) {
      setShowTreatmentSection(true);
    }
    if (props.forceDestinationSection) {
      setShowDestinationSection(true);
    }
  }, [
    props.forceDestinationSection,
    props.forceEquipmentSection,
    props.forcePackagingSection,
    props.forceTreatmentSection,
    props.forceVehicleSection,
  ]);

  useEffect(() => {
    if (props.defaultShowPackagingSection !== undefined) {
      setShowPackagingSection(props.defaultShowPackagingSection);
    }
    if (props.defaultShowEquipmentSection !== undefined) {
      setShowEquipmentSection(props.defaultShowEquipmentSection);
    }
    if (props.defaultShowVehicleSection !== undefined) {
      setShowVehicleSection(props.defaultShowVehicleSection);
    }
    if (props.defaultShowTreatmentSection !== undefined) {
      setShowTreatmentSection(props.defaultShowTreatmentSection);
    }
    if (props.defaultShowDestinationSection !== undefined) {
      setShowDestinationSection(props.defaultShowDestinationSection);
    }
  }, [
    props.defaultShowDestinationSection,
    props.defaultShowEquipmentSection,
    props.defaultShowPackagingSection,
    props.defaultShowTreatmentSection,
    props.defaultShowVehicleSection,
  ]);

  const handleAddPlan = () => {
    if (!props.onAddPlan && !props.onAddPlans) {
      return;
    }

    if (isAddPlanDisabled) {
      return;
    }

    const nextPlanTypes: ProposalResiduePlanCodeEnum[] = [];

    if (hasMobilizationSwitches && !hasMobilizationPlan) {
      nextPlanTypes.push(ProposalResiduePlanCodeEnum.Mobilization);
    }

    if (hasRegularSwitches) {
      nextPlanTypes.push(ProposalResiduePlanCodeEnum.Regular);
    }

    if (!nextPlanTypes.length) {
      return;
    }

    if (props.onAddPlans) {
      props.onAddPlans(nextPlanTypes);
      return;
    }

    nextPlanTypes.forEach((codigoPlanoTipo) => props.onAddPlan?.(codigoPlanoTipo));
  };

  const openEquipmentModal = (
    plan: ProposalResiduePlanType,
    item?: ProposalResiduePlanEquipmentType | null
  ) => {
    setSelectedEquipmentPlan(plan);
    setSelectedEquipmentItem(item || null);
    setEquipmentModalOpened(true);
  };
  const openPackagingModal = (
    plan: ProposalResiduePlanType,
    item?: ProposalResiduePlanPackagingType | null
  ) => {
    setSelectedPackagingPlan(plan);
    setSelectedPackagingItem(item || null);
    setPackagingModalOpened(true);
  };

  const currentSelectedPackagingPlan = selectedPackagingPlan
    ? props.data.planos.find(
        (plan) =>
          `${plan.id || plan.idPropostaResiduoPlano?.toString() || plan.idTemporaria || ''}` ===
          `${
            selectedPackagingPlan.id ||
            selectedPackagingPlan.idPropostaResiduoPlano?.toString() ||
            selectedPackagingPlan.idTemporaria ||
            ''
          }`
      ) || selectedPackagingPlan
    : null;
  const currentSelectedEquipmentPlan = selectedEquipmentPlan
    ? props.data.planos.find(
        (plan) =>
          `${plan.id || plan.idPropostaResiduoPlano?.toString() || plan.idTemporaria || ''}` ===
          `${
            selectedEquipmentPlan.id ||
            selectedEquipmentPlan.idPropostaResiduoPlano?.toString() ||
            selectedEquipmentPlan.idTemporaria ||
            ''
          }`
      ) || selectedEquipmentPlan
    : null;
  const currentSelectedVehiclePlan = selectedVehiclePlan
    ? props.data.planos.find(
        (plan) =>
          `${plan.id || plan.idPropostaResiduoPlano?.toString() || plan.idTemporaria || ''}` ===
          `${
            selectedVehiclePlan.id ||
            selectedVehiclePlan.idPropostaResiduoPlano?.toString() ||
            selectedVehiclePlan.idTemporaria ||
            ''
          }`
      ) || selectedVehiclePlan
    : null;
  const currentSelectedTreatmentPlan = selectedTreatmentPlan
    ? props.data.planos.find(
        (plan) =>
          `${plan.id || plan.idPropostaResiduoPlano?.toString() || plan.idTemporaria || ''}` ===
          `${
            selectedTreatmentPlan.id ||
            selectedTreatmentPlan.idPropostaResiduoPlano?.toString() ||
            selectedTreatmentPlan.idTemporaria ||
            ''
          }`
      ) || selectedTreatmentPlan
    : null;
  const currentSelectedDestinationPlan = selectedDestinationPlan
    ? props.data.planos.find(
        (plan) =>
          `${plan.id || plan.idPropostaResiduoPlano?.toString() || plan.idTemporaria || ''}` ===
          `${
            selectedDestinationPlan.id ||
            selectedDestinationPlan.idPropostaResiduoPlano?.toString() ||
            selectedDestinationPlan.idTemporaria ||
            ''
          }`
      ) || selectedDestinationPlan
    : null;
  const openVehicleModal = (plan: ProposalResiduePlanType, item?: ProposalResiduePlanVehicleType | null) => {
    setSelectedVehiclePlan(plan);
    setSelectedVehicleItem(item || null);
    setVehicleModalOpened(true);
  };
  const openTreatmentModal = (
    plan: ProposalResiduePlanType,
    item?: ProposalResiduePlanTreatmentType | null
  ) => {
    setSelectedTreatmentPlan(plan);
    setSelectedTreatmentItem(item || null);
    setTreatmentModalOpened(true);
  };
  const openDestinationModal = (
    plan: ProposalResiduePlanType,
    item?: ProposalResiduePlanDestinationType | null
  ) => {
    setSelectedDestinationPlan(plan);
    setSelectedDestinationItem(item || null);
    setDestinationModalOpened(true);
  };

  const getQuotationItemRows = (
    plan: ProposalResiduePlanType,
    config: {
      tipo: string;
      itemLabel: string;
      planoQuantidade: string;
      showSection: boolean;
      planItemId?: string | number | null;
      planItemTempId?: string | number | null;
      planRelatedId?: string | number | null;
      planResidueId?: string | number | null;
      quotationItems: Array<{
        action?: Action;
        primario?: boolean;
        id?: string | number | null;
        idTemporaria?: string | number | null;
        relatedId?: string | number | null;
        residueId?: string | number | null;
        fornecedor?: string | null;
        quantidadeLabel?: string | null;
        preco?: number | null;
        precificacao: {
          preco: string;
          quantidadeLabel: string;
        };
      }>;
    }
  ) => {
    if (!config.showSection) {
      return [];
    }

    const activeQuotationItems = config.quotationItems.filter(
      (item) => (item.action || Action.Nothing) !== Action.Delete
    );

    const matchesPlanItem = (quotationItem: {
      id?: string | number | null;
      idTemporaria?: string | number | null;
      relatedId?: string | number | null;
      residueId?: string | number | null;
    }) => {
      const planKeys = [
        config.planItemId,
        config.planItemTempId,
        config.planRelatedId,
        config.planResidueId,
      ].filter((value) => value !== null && value !== undefined && value !== '');
      const quotationKeys = [
        quotationItem.id,
        quotationItem.idTemporaria,
        quotationItem.relatedId,
        quotationItem.residueId,
      ].filter((value) => value !== null && value !== undefined && value !== '');

      return planKeys.some((planKey) =>
        quotationKeys.some((quotationKey) => `${planKey}` === `${quotationKey}`)
      );
    };

    const matchedQuotationItems = activeQuotationItems.filter(matchesPlanItem);

    if (!matchedQuotationItems.length) {
      return [
        {
          key: `${getPlanKey(plan)}-${config.tipo}-${
            config.planRelatedId || config.planItemTempId || config.itemLabel
          }`,
          tipo: config.tipo,
          item: config.itemLabel || '-',
          planoQuantidade: config.planoQuantidade || '-',
          fornecedor: '-',
          cotacaoQuantidade: '-',
          isPrimaryQuotation: false,
          valorFornecedor: '-',
          precificacao: {
            preco: '-',
            quantidadeLabel: '-',
          },
        },
      ];
    }

    return matchedQuotationItems.map((quotationItem, index) => ({
      key: `${getPlanKey(plan)}-${config.tipo}-${
        quotationItem.id || quotationItem.relatedId || quotationItem.idTemporaria || 'row'
      }-${index}`,
      tipo: config.tipo,
      item: config.itemLabel || '-',
      planoQuantidade: config.planoQuantidade || '-',
      fornecedor: quotationItem.fornecedor || '-',
      cotacaoQuantidade: quotationItem.quantidadeLabel || '-',
      isPrimaryQuotation: !!quotationItem.primario,
      valorFornecedor:
        quotationItem.preco === null || quotationItem.preco === undefined
          ? '-'
          : formatCurrency(Number(quotationItem.preco)),
      precificacao: quotationItem.precificacao,
    }));
  };

  const buildEquipmentCard = (plan: ProposalResiduePlanType, danger?: boolean) => {
    const items = plan.equipamentos.filter((x) => (x.action || Action.Nothing) !== Action.Delete);

    return (
      <Paper
        p="xs"
        withBorder
        radius="md"
        style={{
          background: danger ? '#fff1f0' : '#fbfcfd',
          borderColor: danger ? '#f08c82' : undefined,
          minHeight: 118,
        }}
      >
        <Stack gap={6}>
          <Group justify="space-between" align="center">
            <Group gap={6}>
              <IconBackhoe size={15} />
              <Text size="sm" fw={500}>
                Equipamentos
              </Text>
            </Group>
            {props.isEdit && props.referenceData && !props.disableAddEquipmentAction ? (
              <Tooltip label="Adicionar equipamento" withArrow>
                <ActionIcon size="sm" variant="light" color="blue" onClick={() => openEquipmentModal(plan)}>
                  <IconPlus size={15} />
                </ActionIcon>
              </Tooltip>
            ) : (
              <Badge variant="outline" size="sm">
                {items.length}
              </Badge>
            )}
          </Group>

          {!items.length && (
            <Text size="xs" c="dimmed">
              Nenhum item.
            </Text>
          )}

          {items.map((item) => {
            const itemKey = item.id || item.idPropostaResiduoPlanoEquipamento || item.idTemporaria || '';
            const canOpenEquipmentModal = !!props.referenceData;

            return (
              <Group key={`Equipamentos_${itemKey}`} justify="space-between" gap={6} wrap="nowrap">
                <Group gap={4} className="custom-mantine-badge-group-down" wrap="nowrap">
                  <Badge variant="outline" size="sm">
                    {item.residuoEquipamento}
                  </Badge>
                  <Badge variant="outline" size="sm">
                    {item.quantidade}
                  </Badge>
                </Group>
                <Group gap={4} wrap="nowrap">
                  {canOpenEquipmentModal ? (
                    <Tooltip label={`${itemActionLabel} equipamento`} withArrow>
                      <ActionIcon size="sm" variant="light" onClick={() => openEquipmentModal(plan, item)}>
                        <ActionIconComponent size={15} />
                      </ActionIcon>
                    </Tooltip>
                  ) : (
                    props.onPlanItemAction && (
                      <Tooltip label={`${itemActionLabel} equipamento`} withArrow>
                        <ActionIcon
                          size="sm"
                          variant="light"
                          onClick={() => props.onPlanItemAction!(plan, 'Equipamentos', itemKey, 'open')}
                        >
                          <ActionIconComponent size={15} />
                        </ActionIcon>
                      </Tooltip>
                    )
                  )}
                  {props.isEdit && props.onPlanItemDelete && (
                    <DeleteConfirmPopover
                      label="Excluir equipamento"
                      message="Esta ação removerá o equipamento do plano."
                      onConfirm={() => props.onPlanItemDelete!(plan, 'Equipamentos', itemKey)}
                    />
                  )}
                </Group>
              </Group>
            );
          })}
        </Stack>
      </Paper>
    );
  };

  const buildPackagingCard = (plan: ProposalResiduePlanType, danger?: boolean) => {
    const items = plan.acondicionamentos.filter((x) => (x.action || Action.Nothing) !== Action.Delete);
    const canOpenModal = !!props.referenceData && !!props.services;
    return (
      <Paper
        p="xs"
        withBorder
        radius="md"
        style={{
          background: danger ? '#fff1f0' : '#fbfcfd',
          borderColor: danger ? '#f08c82' : undefined,
          minHeight: 118,
        }}
      >
        <Stack gap={6}>
          <Group justify="space-between" align="center">
            <Group gap={6}>
              <IconPackage size={15} />
              <Text size="sm" fw={500}>
                Acondicionamentos
              </Text>
            </Group>
            {props.isEdit && canOpenModal && !props.disableAddPackagingAction ? (
              <Tooltip label="Adicionar acondicionamento" withArrow>
                <ActionIcon size="sm" variant="light" color="blue" onClick={() => openPackagingModal(plan)}>
                  <IconPlus size={15} />
                </ActionIcon>
              </Tooltip>
            ) : (
              <Badge variant="outline" size="sm">
                {items.length}
              </Badge>
            )}
          </Group>
          {!items.length && (
            <Text size="xs" c="dimmed">
              Nenhum item.
            </Text>
          )}
          {items.map((item) => {
            const itemKey = item.id || item.idPropostaResiduoPlanoAcondicionamento || item.idTemporaria || '';
            return (
              <Group key={`Acondicionamentos_${itemKey}`} justify="space-between" gap={6} wrap="nowrap">
                <Group gap={4} className="custom-mantine-badge-group-down" wrap="nowrap">
                  {item.gerenciar ? <IconBriefcase size={14} /> : null}
                  {item.compartilhado ? <IconShare size={14} /> : null}
                  <Badge variant="outline" size="sm">
                    {item.residuoAcondicionamento}
                  </Badge>
                  <Badge variant="outline" size="sm">
                    {item.quantidade}
                  </Badge>
                </Group>
                <Group gap={4} wrap="nowrap">
                  {canOpenModal ? (
                    <Tooltip label={`${itemActionLabel} acondicionamento`} withArrow>
                      <ActionIcon size="sm" variant="light" onClick={() => openPackagingModal(plan, item)}>
                        <ActionIconComponent size={15} />
                      </ActionIcon>
                    </Tooltip>
                  ) : (
                    props.onPlanItemAction && (
                      <Tooltip label={`${itemActionLabel} acondicionamento`} withArrow>
                        <ActionIcon
                          size="sm"
                          variant="light"
                          onClick={() => props.onPlanItemAction!(plan, 'Acondicionamentos', itemKey, 'open')}
                        >
                          <ActionIconComponent size={15} />
                        </ActionIcon>
                      </Tooltip>
                    )
                  )}
                  {props.isEdit && props.onPlanItemDelete && (
                    <DeleteConfirmPopover
                      label="Excluir acondicionamento"
                      message="Esta ação removerá o acondicionamento do plano."
                      onConfirm={() => props.onPlanItemDelete!(plan, 'Acondicionamentos', itemKey)}
                    />
                  )}
                </Group>
              </Group>
            );
          })}
        </Stack>
      </Paper>
    );
  };

  const buildSingleItemCard = <
    T extends { id?: string | number; idTemporaria?: string | number; action?: Action }
  >(
    plan: ProposalResiduePlanType,
    config: {
      label: string;
      icon: typeof IconPackage;
      item: T | null;
      itemId: string | number;
      itemLabel: string;
      danger?: boolean;
      openModal: (planItem: ProposalResiduePlanType, item?: T | null) => void;
      onFallbackOpen: () => void;
      onDelete: (itemId: string | number) => void;
    }
  ) => {
    const isVisible = !!config.item && (config.item.action || Action.Nothing) !== Action.Delete;
    const canOpenModal = !!props.referenceData;
    return (
      <Paper
        p="xs"
        withBorder
        radius="md"
        style={{
          background: config.danger ? '#fff1f0' : '#fbfcfd',
          borderColor: config.danger ? '#f08c82' : undefined,
          minHeight: 118,
        }}
      >
        <Stack gap={6}>
          <Group justify="space-between" align="center">
            <Group gap={6}>
              <config.icon size={15} />
              <Text size="sm" fw={500}>
                {config.label}
              </Text>
            </Group>
            {props.isEdit && !isVisible && (canOpenModal || props.onPlanItemAction) ? (
              <Tooltip label={`Adicionar ${config.label.toLowerCase()}`} withArrow>
                <ActionIcon
                  size="sm"
                  variant="light"
                  color="blue"
                  onClick={() =>
                    canOpenModal
                      ? config.openModal(plan)
                      : props.onPlanItemAction?.(plan, config.label, null, 'add')
                  }
                >
                  <IconPlus size={15} />
                </ActionIcon>
              </Tooltip>
            ) : (
              <Badge variant="outline" size="sm">
                {isVisible ? 1 : 0}
              </Badge>
            )}
          </Group>
          {!isVisible && (
            <Text size="xs" c="dimmed">
              Nenhum item.
            </Text>
          )}
          {isVisible && config.item && (
            <Group justify="space-between" gap={6} wrap="nowrap">
              {config.label.startsWith('Ve') ||
              config.label.startsWith('Tra') ||
              config.label.startsWith('Des') ? (
                <Group gap={4} className="custom-mantine-badge-group-down" wrap="nowrap">
                  {'autorizacaoAmbiental' in config.item && <IconCheck size={14} />}
                  <Badge variant="outline" size="sm" style={{ maxWidth: 180 }}>
                    {config.itemLabel}
                  </Badge>
                  {'quantidade' in config.item && (
                    <Badge variant="outline" size="sm">
                      {(config.item as any).quantidade}
                    </Badge>
                  )}
                  {/* {'cargaMedia' in config.item && (
                    <Badge variant="outline" size="sm">
                      {(config.item as ProposalResiduePlanVehicleType).cargaMedia}
                    </Badge>
                  )} */}
                </Group>
              ) : (
                <Text size="xs" lineClamp={1}>
                  {config.itemLabel}
                </Text>
              )}
              <Group gap={4} wrap="nowrap">
                {canOpenModal ? (
                  <Tooltip label={`${itemActionLabel} ${config.label.toLowerCase()}`} withArrow>
                    <ActionIcon size="sm" variant="light" onClick={() => config.openModal(plan, config.item)}>
                      <ActionIconComponent size={15} />
                    </ActionIcon>
                  </Tooltip>
                ) : (
                  props.onPlanItemAction && (
                    <Tooltip label={`${itemActionLabel} ${config.label.toLowerCase()}`} withArrow>
                      <ActionIcon size="sm" variant="light" onClick={config.onFallbackOpen}>
                        <ActionIconComponent size={15} />
                      </ActionIcon>
                    </Tooltip>
                  )
                )}
                {props.isEdit && props.onPlanItemDelete && (
                  <DeleteConfirmPopover
                    label={`Excluir ${config.label.toLowerCase()}`}
                    message={`Esta ação removerá ${config.label.toLowerCase()} do plano.`}
                    onConfirm={() => config.onDelete(config.itemId)}
                  />
                )}
              </Group>
            </Group>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <>
      <Paper p="md" withBorder radius="lg">
        <Stack gap="sm">
          {!props.hideHeader && (
            <Group justify="space-between">
              <Group gap="xs">
                <IconSchema size={16} />
                <Text fw={600}>
                  {proposalResidueData?.residuoCliente ?? props.data.residuoCliente ?? 'Planos'}
                </Text>
              </Group>
              <Group gap="xs">
                {props.headerRightContent}
                {props.isEdit && props.onAddPlan && (
                  <Tooltip label="Adicionar plano" withArrow>
                    <ActionIcon
                      size="sm"
                      color="blue"
                      variant="light"
                      onClick={handleAddPlan}
                      disabled={isAddPlanDisabled}
                    >
                      <IconPlus size={15} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Group>
          )}

          {!props.hideSectionSelectors && (
            <Paper p="md" withBorder radius="lg">
              <Group justify="center" gap="md" wrap="wrap">
                <Switch
                  label="Acondicionamento"
                  checked={packagingSectionEnabled}
                  disabled={!!props.hidePackagingSection || !!props.forcePackagingSection || !props.isEdit}
                  onChange={(event) => setShowPackagingSection(event.currentTarget.checked)}
                />
                <Switch
                  label="Equipamento"
                  checked={equipmentSectionEnabled}
                  disabled={!!props.hideEquipmentSection || !!props.forceEquipmentSection || !props.isEdit}
                  onChange={(event) => setShowEquipmentSection(event.currentTarget.checked)}
                />
                <Switch
                  label="Veículo"
                  checked={vehicleSectionEnabled}
                  disabled={!!props.hideVehicleSection || !!props.forceVehicleSection || !props.isEdit}
                  onChange={(event) => setShowVehicleSection(event.currentTarget.checked)}
                />
                <Switch
                  label="Tratamento"
                  checked={treatmentSectionEnabled}
                  disabled={!!props.hideTreatmentSection || !!props.forceTreatmentSection || !props.isEdit}
                  onChange={(event) => setShowTreatmentSection(event.currentTarget.checked)}
                />
                <Switch
                  label="Destino final"
                  checked={destinationSectionEnabled}
                  disabled={
                    !!props.hideDestinationSection || !!props.forceDestinationSection || !props.isEdit
                  }
                  onChange={(event) => setShowDestinationSection(event.currentTarget.checked)}
                />
              </Group>
            </Paper>
          )}

          {!visiblePlans.length && (
            <Text size="sm" c="dimmed">
              Nenhum plano cadastrado.
            </Text>
          )}

          <Stack gap="md">
            {visiblePlans.map((plan, index) => {
              const visibleQuotations = [...plan.cotacoes].filter(
                (x) => (x.action || Action.Nothing) !== Action.Delete
              );
              const hasCompleteQuotation = visibleQuotations.some((x) =>
                isQuotationEffectivelyComplete(plan, x)
              );
              const mobilizationPlan = isMobilizationPlan(plan);
              const showPackagingCard =
                mobilizationPlan && !props.hidePackagingSection && packagingSectionEnabled;
              const showEquipmentCard =
                mobilizationPlan && !props.hideEquipmentSection && equipmentSectionEnabled;
              const showVehicleCard = !mobilizationPlan && !props.hideVehicleSection && vehicleSectionEnabled;
              const showTreatmentCard =
                !mobilizationPlan && !props.hideTreatmentSection && treatmentSectionEnabled;
              const showDestinationCard =
                !mobilizationPlan && !props.hideDestinationSection && destinationSectionEnabled;
              const hasPackagingItem = plan.acondicionamentos.some(
                (x) => (x.action || Action.Nothing) !== Action.Delete
              );
              const hasEquipmentItem = plan.equipamentos.some(
                (x) => (x.action || Action.Nothing) !== Action.Delete
              );
              const hasVehicleItem =
                !!plan.veiculo && (plan.veiculo.action || Action.Nothing) !== Action.Delete;
              const hasTreatmentItem =
                !!plan.tratamento && (plan.tratamento.action || Action.Nothing) !== Action.Delete;
              const hasDestinationItem =
                !!plan.destinoFinal && (plan.destinoFinal.action || Action.Nothing) !== Action.Delete;
              const masterPlanMissingDefinition = mobilizationPlan && !hasPackagingItem && !hasEquipmentItem;
              const regularPlanMissingDefinition =
                !mobilizationPlan && !hasVehicleItem && !hasTreatmentItem && !hasDestinationItem;
              const visiblePlanSections = Math.max(
                [
                  showPackagingCard,
                  showEquipmentCard,
                  showVehicleCard,
                  showTreatmentCard,
                  showDestinationCard,
                ].filter(Boolean).length,
                1
              );
              const planSummaryRows = [
                ...plan.acondicionamentos
                  .filter((item) => (item.action || Action.Nothing) !== Action.Delete)
                  .flatMap((item) =>
                    getQuotationItemRows(plan, {
                      tipo: 'Acondicionamento',
                      itemLabel: item.residuoAcondicionamento,
                      planoQuantidade: item.compartilhado
                        ? '%'
                        : `${item.quantidade} ${item.quantidadeUnidadeMedida}`,
                      showSection: showPackagingCard,
                      planItemId: item.id,
                      planItemTempId: item.idTemporaria,
                      planRelatedId: item.idPropostaResiduoPlanoAcondicionamento,
                      planResidueId: item.idResiduoAcondicionamento,
                      quotationItems: visibleQuotations.flatMap((quotation) =>
                        quotation.acondicionamentos.map((quotationItem) => ({
                          action: quotationItem.action,
                          id: quotationItem.id,
                          idTemporaria: quotationItem.idTemporaria,
                          relatedId: quotationItem.idPropostaResiduoPlanoAcondicionamento,
                          residueId: quotationItem.idResiduoAcondicionamento,
                          primario: quotation.primario,
                          fornecedor:
                            quotationItem.fornecedorNomeFantasia ||
                            quotationItem.fornecedorRazaoSocial ||
                            quotationItem.fornecedorNome ||
                            null,
                          quantidadeLabel: `${quotationItem.quantidade || '-'} ${
                            quotationItem.quantidadeUnidadeMedida || '-'
                          } / ${quotationItem.frequenciaUnidadeMedida || '-'}`,
                          preco: quotationItem.preco,
                          precificacao: {
                            preco: formatCurrency(quotationItem.precificacao.preco),
                            quantidadeLabel: `${quotationItem.precificacao.quantidade || '-'} ${
                              quotationItem.precificacao.quantidadeUnidadeMedida || '-'
                            } / ${quotationItem.precificacao.frequenciaUnidadeMedida || '-'}`,
                          },
                        }))
                      ),
                    })
                  ),
                ...plan.equipamentos
                  .filter((item) => (item.action || Action.Nothing) !== Action.Delete)
                  .flatMap((item) =>
                    getQuotationItemRows(plan, {
                      tipo: 'Equipamento',
                      itemLabel: item.residuoEquipamento,
                      planoQuantidade: `${item.quantidade} ${item.quantidadeUnidadeMedida}`,
                      showSection: showEquipmentCard,
                      planItemId: item.id,
                      planItemTempId: item.idTemporaria,
                      planRelatedId: item.idPropostaResiduoPlanoEquipamento,
                      planResidueId: item.idResiduoEquipamento,
                      quotationItems: visibleQuotations.flatMap((quotation) =>
                        quotation.equipamentos.map((quotationItem) => ({
                          action: quotationItem.action,
                          id: quotationItem.id,
                          idTemporaria: quotationItem.idTemporaria,
                          relatedId: quotationItem.idPropostaResiduoPlanoEquipamento,
                          residueId: quotationItem.idResiduoEquipamento,
                          primario: quotation.primario,
                          fornecedor:
                            quotationItem.fornecedorNomeFantasia ||
                            quotationItem.fornecedorRazaoSocial ||
                            quotationItem.fornecedorNome ||
                            null,
                          quantidadeLabel: `${quotationItem.quantidade || '-'} ${
                            quotationItem.quantidadeUnidadeMedida || '-'
                          } / ${quotationItem.frequenciaUnidadeMedida || '-'}`,
                          preco: quotationItem.preco,
                          precificacao: {
                            preco: formatCurrency(quotationItem.precificacao.preco),
                            quantidadeLabel: `${quotationItem.precificacao.quantidade || '-'} ${
                              quotationItem.precificacao.quantidadeUnidadeMedida || '-'
                            } / ${quotationItem.precificacao.frequenciaUnidadeMedida || '-'}`,
                          },
                        }))
                      ),
                    })
                  ),
                ...(plan.veiculo && (plan.veiculo.action || Action.Nothing) !== Action.Delete
                  ? getQuotationItemRows(plan, {
                      tipo: 'Veículo',
                      itemLabel: plan.veiculo.residuoVeiculo,
                      planoQuantidade: `${plan.veiculo.quantidade} ${plan.veiculo.quantidadeUnidadeMedida}`,
                      showSection: showVehicleCard,
                      planItemId: plan.veiculo.id,
                      planItemTempId: plan.veiculo.idTemporaria,
                      planRelatedId: plan.veiculo.idPropostaResiduoPlanoVeiculo,
                      planResidueId: plan.veiculo.idResiduoVeiculo,
                      quotationItems: visibleQuotations.flatMap((quotation) =>
                        quotation.veiculo
                          ? [
                              {
                                action: quotation.veiculo.action,
                                id: quotation.veiculo.id,
                                idTemporaria: quotation.veiculo.idTemporaria,
                                relatedId: quotation.veiculo.idPropostaResiduoPlanoVeiculo,
                                residueId: quotation.veiculo.idResiduoVeiculo,
                                primario: quotation.primario,
                                fornecedor:
                                  quotation.veiculo.fornecedorNomeFantasia ||
                                  quotation.veiculo.fornecedorRazaoSocial ||
                                  quotation.veiculo.fornecedorNome ||
                                  null,
                                quantidadeLabel: `${quotation.veiculo.quantidade || '-'} ${
                                  quotation.veiculo.quantidadeUnidadeMedida || '-'
                                } / ${quotation.veiculo.frequenciaUnidadeMedida || '-'}`,
                                preco: quotation.veiculo.preco,
                                precificacao: {
                                  preco: formatCurrency(quotation.veiculo.precificacao.preco),
                                  quantidadeLabel: `${quotation.veiculo.precificacao.quantidade || '-'} ${
                                    quotation.veiculo.precificacao.quantidadeUnidadeMedida || '-'
                                  } / ${quotation.veiculo.precificacao.frequenciaUnidadeMedida || '-'}`,
                                },
                              },
                            ]
                          : []
                      ),
                    })
                  : []),
                ...(plan.tratamento && (plan.tratamento.action || Action.Nothing) !== Action.Delete
                  ? getQuotationItemRows(plan, {
                      tipo: 'Tratamento',
                      itemLabel: plan.tratamento.residuoTratamento,
                      planoQuantidade: '-',
                      showSection: showTreatmentCard,
                      planItemId: plan.tratamento.id,
                      planItemTempId: plan.tratamento.idTemporaria,
                      planRelatedId: plan.tratamento.idPropostaResiduoPlanoTratamento,
                      planResidueId: plan.tratamento.idResiduoTratamento,
                      quotationItems: visibleQuotations.flatMap((quotation) =>
                        quotation.tratamento
                          ? [
                              {
                                action: quotation.tratamento.action,
                                id: quotation.tratamento.id,
                                idTemporaria: quotation.tratamento.idTemporaria,
                                relatedId: quotation.tratamento.idPropostaResiduoPlanoTratamento,
                                residueId: quotation.tratamento.idResiduoTratamento,
                                primario: quotation.primario,
                                fornecedor:
                                  quotation.tratamento.fornecedorNomeFantasia ||
                                  quotation.tratamento.fornecedorRazaoSocial ||
                                  quotation.tratamento.fornecedorNome ||
                                  null,
                                quantidadeLabel: `${quotation.tratamento.quantidade || '-'} ${
                                  quotation.tratamento.quantidadeUnidadeMedida || '-'
                                }`,
                                preco: quotation.tratamento.preco,
                                precificacao: {
                                  preco: formatCurrency(quotation.tratamento.precificacao.preco),
                                  quantidadeLabel: `${quotation.tratamento.precificacao.quantidade || '-'} ${
                                    quotation.tratamento.precificacao.quantidadeUnidadeMedida
                                  }`,
                                },
                              },
                            ]
                          : []
                      ),
                    })
                  : []),
                ...(plan.destinoFinal && (plan.destinoFinal.action || Action.Nothing) !== Action.Delete
                  ? getQuotationItemRows(plan, {
                      tipo: 'Destino final',
                      itemLabel: plan.destinoFinal.residuoDestinoFinal,
                      planoQuantidade: '-',
                      showSection: showDestinationCard,
                      planItemId: plan.destinoFinal.id,
                      planItemTempId: plan.destinoFinal.idTemporaria,
                      planRelatedId: plan.destinoFinal.idPropostaResiduoPlanoDestinoFinal,
                      planResidueId: plan.destinoFinal.idResiduoDestinoFinal,
                      quotationItems: visibleQuotations.flatMap((quotation) =>
                        quotation.destinoFinal
                          ? [
                              {
                                action: quotation.destinoFinal.action,
                                id: quotation.destinoFinal.id,
                                idTemporaria: quotation.destinoFinal.idTemporaria,
                                relatedId: quotation.destinoFinal.idPropostaResiduoPlanoDestinoFinal,
                                residueId: quotation.destinoFinal.idResiduoDestinoFinal,
                                primario: quotation.primario,
                                fornecedor:
                                  quotation.destinoFinal.fornecedorNomeFantasia ||
                                  quotation.destinoFinal.fornecedorRazaoSocial ||
                                  quotation.destinoFinal.fornecedorNome ||
                                  null,
                                quantidadeLabel: `${quotation.destinoFinal.quantidade || '-'} ${
                                  quotation.destinoFinal.quantidadeUnidadeMedida || '-'
                                }`,
                                preco: quotation.destinoFinal.preco,
                                precificacao: {
                                  preco: formatCurrency(quotation.destinoFinal.precificacao.preco),
                                  quantidadeLabel: `${
                                    quotation.destinoFinal.precificacao.quantidade || '-'
                                  } ${quotation.destinoFinal.precificacao.quantidadeUnidadeMedida}`,
                                },
                              },
                            ]
                          : []
                      ),
                    })
                  : []),
              ];
              const groupedPlanSummaryRows = planSummaryRows.reduce<
                Array<{
                  key: string;
                  tipo: string;
                  item: string;
                  planoQuantidade: string;
                  rows: typeof planSummaryRows;
                }>
              >((acc, row) => {
                const groupKey = `${row.tipo}|${row.item}|${row.planoQuantidade}`;
                const existing = acc.find((entry) => entry.key === groupKey);

                if (existing) {
                  existing.rows.push(row);
                  return acc;
                }

                acc.push({
                  key: groupKey,
                  tipo: row.tipo,
                  item: row.item,
                  planoQuantidade: row.planoQuantidade,
                  rows: [row],
                });

                return acc;
              }, []);
              let planBackground = '#fcfcfd';
              let planBorderColor: string | undefined;

              if (mobilizationPlan) {
                planBackground = '#f4f8ff';
                planBorderColor = '#bac8ff';
              } else if (plan.primario) {
                planBackground = '#f8fff9';
                planBorderColor = '#b7e4c7';
              }

              const planHeader = (
                <Stack gap={6}>
                  <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                    Plano
                  </Text>
                  <Group gap="xs" className="custom-mantine-badge-group-down">
                    <Text fw={700}>#{getPlanDisplayNumber(plan, index)}</Text>
                    <Badge color={isMobilizationPlan(plan) ? 'blue' : 'grape'} variant="outline" size="sm">
                      {isMobilizationPlan(plan) ? 'Mobilização' : 'Regular'}
                    </Badge>
                    {plan.primario && (
                      <Badge color="green" variant="light" size="sm">
                        Primário
                      </Badge>
                    )}
                    {!visibleQuotations.some((x) => x.primario) && (
                      <Badge color="orange" variant="light" size="sm">
                        Cotação primária pendente
                      </Badge>
                    )}
                    {!hasCompleteQuotation && (
                      <Badge color="orange" variant="light" size="sm">
                        Cotação incompleta
                      </Badge>
                    )}
                  </Group>
                </Stack>
              );

              const planActions = (
                <>
                  {props.isEdit && !mobilizationPlan && !plan.primario && props.onSetPrimaryPlan && (
                    <Tooltip label="Definir como primário" withArrow>
                      <ActionIcon
                        size="sm"
                        color="green"
                        variant="light"
                        disabled={!hasCompletePlanDefinition(plan)}
                        onClick={() => props.onSetPrimaryPlan!(plan)}
                      >
                        <IconCheck size={15} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {props.isEdit && props.onDeletePlan && (
                    <DeleteConfirmPopover
                      label="Excluir plano"
                      message="Esta ação removerá o plano da proposta."
                      onConfirm={() => props.onDeletePlan!(plan)}
                    />
                  )}
                </>
              );

              const summaryContent = (
                <Stack gap="xs">
                  <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                    Resumo Geral
                  </Text>
                  <MantineTable
                    withTableBorder
                    withColumnBorders
                    highlightOnHover
                    horizontalSpacing="sm"
                    verticalSpacing="xs"
                  >
                    <MantineTable.Thead>
                      <MantineTable.Tr style={{ background: '#eef2f6' }}>
                        <MantineTable.Th style={{ color: '#495057', fontWeight: 700 }}>Tipo</MantineTable.Th>
                        <MantineTable.Th style={{ color: '#495057', fontWeight: 700 }}>Item</MantineTable.Th>
                        <MantineTable.Th style={{ color: '#495057', fontWeight: 700 }}>Plano</MantineTable.Th>
                        <MantineTable.Th style={{ color: '#495057', fontWeight: 700 }}>
                          Fornecedor
                        </MantineTable.Th>
                        <MantineTable.Th style={{ color: '#495057', fontWeight: 700 }}>
                          Valor Fornecedor
                        </MantineTable.Th>
                        <MantineTable.Th style={{ color: '#495057', fontWeight: 700 }}>
                          Valor Cliente
                        </MantineTable.Th>
                      </MantineTable.Tr>
                    </MantineTable.Thead>
                    <MantineTable.Tbody>
                      {groupedPlanSummaryRows.length ? (
                        groupedPlanSummaryRows.flatMap((group) =>
                          group.rows.map((row, rowIndex) => {
                            const isLastRow = rowIndex === group.rows.length - 1;
                            const rowStyle = {
                              background: rowIndex % 2 === 0 ? '#fcfcfd' : '#f8fafc',
                              borderBottom: isLastRow ? '2px solid #cfd8e3' : '1px solid #e9ecef',
                            };

                            return (
                              <MantineTable.Tr key={row.key} style={rowStyle}>
                                {rowIndex === 0 ? (
                                  <>
                                    <MantineTable.Td
                                      rowSpan={group.rows.length}
                                      style={{
                                        background: '#f1f3f5',
                                        fontWeight: 500,
                                        verticalAlign: 'top',
                                      }}
                                    >
                                      {group.tipo}
                                    </MantineTable.Td>
                                    <MantineTable.Td
                                      rowSpan={group.rows.length}
                                      style={{ background: '#f8f9fa', verticalAlign: 'top' }}
                                    >
                                      {group.item}
                                    </MantineTable.Td>
                                    <MantineTable.Td
                                      rowSpan={group.rows.length}
                                      style={{
                                        background: '#f8f9fa',
                                        whiteSpace: 'nowrap',
                                        verticalAlign: 'top',
                                      }}
                                    >
                                      {group.planoQuantidade}
                                    </MantineTable.Td>
                                  </>
                                ) : null}
                                <MantineTable.Td>{row.fornecedor}</MantineTable.Td>
                                <MantineTable.Td>
                                  <Group gap={6} wrap="nowrap">
                                    {row.isPrimaryQuotation ? (
                                      <Tooltip label="Cotação primária" withArrow>
                                        <IconCheck size={14} color="green" />
                                      </Tooltip>
                                    ) : null}
                                    <Text size="sm">
                                      {row.valorFornecedor !== '-'
                                        ? `${row.valorFornecedor} | ${row.cotacaoQuantidade}`
                                        : '-'}
                                    </Text>
                                  </Group>
                                </MantineTable.Td>
                                <MantineTable.Td style={{ whiteSpace: 'nowrap' }}>
                                  <Text size="sm">
                                    {row.precificacao.preco} | {row.precificacao.quantidadeLabel}
                                  </Text>
                                </MantineTable.Td>
                              </MantineTable.Tr>
                            );
                          })
                        )
                      ) : (
                        <MantineTable.Tr>
                          <MantineTable.Td colSpan={7}>
                            <Text size="sm" c="dimmed" ta="center">
                              Nenhum item disponível para o resumo.
                            </Text>
                          </MantineTable.Td>
                        </MantineTable.Tr>
                      )}
                    </MantineTable.Tbody>
                  </MantineTable>
                </Stack>
              );

              const detailsContent = (
                <Stack gap="md">
                  <Stack gap="xs">
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                      Itens do plano
                    </Text>
                    <SimpleGrid cols={visiblePlanSections} spacing="xs">
                      {showPackagingCard ? buildPackagingCard(plan, masterPlanMissingDefinition) : null}
                      {showEquipmentCard ? buildEquipmentCard(plan, masterPlanMissingDefinition) : null}
                      {showVehicleCard
                        ? buildSingleItemCard(plan, {
                            label: 'Veículo',
                            icon: IconTruck,
                            item: plan.veiculo,
                            danger: regularPlanMissingDefinition,
                            itemId:
                              plan.veiculo?.id ||
                              plan.veiculo?.idPropostaResiduoPlanoVeiculo ||
                              plan.veiculo?.idTemporaria ||
                              '',
                            itemLabel: plan.veiculo?.residuoVeiculo || '',
                            openModal: openVehicleModal,
                            onFallbackOpen: () =>
                              props.onPlanItemAction!(
                                plan,
                                'Veiculos',
                                plan.veiculo?.id || plan.veiculo?.idTemporaria || '',
                                'open'
                              ),
                            onDelete: (itemId) => props.onPlanItemDelete!(plan, 'Veiculos', itemId),
                          })
                        : null}
                      {showTreatmentCard
                        ? buildSingleItemCard(plan, {
                            label: 'Tratamento',
                            icon: IconRecycle,
                            item: plan.tratamento,
                            danger: regularPlanMissingDefinition,
                            itemId:
                              plan.tratamento?.id ||
                              plan.tratamento?.idPropostaResiduoPlanoTratamento ||
                              plan.tratamento?.idTemporaria ||
                              '',
                            itemLabel: plan.tratamento?.residuoTratamento || '',
                            openModal: openTreatmentModal,
                            onFallbackOpen: () =>
                              props.onPlanItemAction!(
                                plan,
                                treatmentTabKey,
                                plan.tratamento?.id || plan.tratamento?.idTemporaria || '',
                                'open'
                              ),
                            onDelete: (itemId) => props.onPlanItemDelete!(plan, treatmentTabKey, itemId),
                          })
                        : null}
                      {showDestinationCard
                        ? buildSingleItemCard(plan, {
                            label: 'Destino final',
                            icon: IconTarget,
                            item: plan.destinoFinal,
                            danger: regularPlanMissingDefinition,
                            itemId:
                              plan.destinoFinal?.id ||
                              plan.destinoFinal?.idPropostaResiduoPlanoDestinoFinal ||
                              plan.destinoFinal?.idTemporaria ||
                              '',
                            itemLabel: plan.destinoFinal?.residuoDestinoFinal || '',
                            openModal: openDestinationModal,
                            onFallbackOpen: () =>
                              props.onPlanItemAction!(
                                plan,
                                'DestinoFinal',
                                plan.destinoFinal?.id || plan.destinoFinal?.idTemporaria || '',
                                'open'
                              ),
                            onDelete: (itemId) => props.onPlanItemDelete!(plan, 'DestinoFinal', itemId),
                          })
                        : null}
                    </SimpleGrid>
                  </Stack>

                  <Stack gap="xs">
                    <Group justify="space-between" align="center">
                      <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                        Cotações
                      </Text>
                      <Group gap={4}>
                        {props.isEdit && props.onAddQuotation && (
                          <Tooltip label="Adicionar Cotação" withArrow>
                            <ActionIcon
                              size="sm"
                              variant="light"
                              color="blue"
                              onClick={() => props.onAddQuotation!(plan)}
                            >
                              <IconPlus size={15} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Group>
                    <ResidueQuotationSummary
                      data={plan}
                      compra={props.data.compra}
                      isEdit={props.isEdit}
                      hidePackagingSection={
                        !mobilizationPlan || props.hidePackagingSection || !packagingSectionEnabled
                      }
                      hideEquipmentSection={
                        !mobilizationPlan || props.hideEquipmentSection || !equipmentSectionEnabled
                      }
                      hideVehicleSection={
                        mobilizationPlan || props.hideVehicleSection || !vehicleSectionEnabled
                      }
                      hideTreatmentSection={
                        mobilizationPlan || props.hideTreatmentSection || !treatmentSectionEnabled
                      }
                      hideDestinationSection={
                        mobilizationPlan || props.hideDestinationSection || !destinationSectionEnabled
                      }
                      referenceData={props.referenceData}
                      services={props.services}
                      zIndex={props.zIndex}
                      requireSupplier={props.requireSupplier}
                      onAdd={props.onAddQuotation ? () => props.onAddQuotation!(plan) : undefined}
                      onSetPrimary={
                        props.onSetPrimaryQuotation
                          ? (quotation) => props.onSetPrimaryQuotation!(plan, quotation)
                          : undefined
                      }
                      onQuotationAction={(quotation) => props.onQuotationAction?.(plan, quotation)}
                      onToleranceAction={(quotation) => props.onQuotationToleranceAction?.(plan, quotation)}
                      onDelete={
                        props.onDeleteQuotation
                          ? (quotation) => props.onDeleteQuotation!(plan, quotation)
                          : undefined
                      }
                      onItemQuotationAction={
                        props.onQuotationItemAction
                          ? (quotation, quotationTab, itemId) =>
                              props.onQuotationItemAction!(plan, quotation, quotationTab, itemId)
                          : undefined
                      }
                      onItemToleranceAction={
                        props.onQuotationItemToleranceAction
                          ? (quotation, quotationTab, itemId) =>
                              props.onQuotationItemToleranceAction!(plan, quotation, quotationTab, itemId)
                          : undefined
                      }
                      onItemDeleteAction={
                        props.onQuotationItemDelete
                          ? (quotation, quotationTab, itemId) =>
                              props.onQuotationItemDelete!(plan, quotation, quotationTab, itemId)
                          : undefined
                      }
                      onAddItemQuotationAction={
                        props.onAddQuotationItem
                          ? (quotation, quotationTab) =>
                              props.onAddQuotationItem!(plan, quotation, quotationTab)
                          : undefined
                      }
                      onEquipmentQuotationSubmit={
                        props.onEquipmentQuotationSubmit
                          ? (quotation, refItem, item, action) =>
                              props.onEquipmentQuotationSubmit!(plan, quotation, refItem, item, action)
                          : undefined
                      }
                      onPackagingQuotationSubmit={
                        props.onPackagingQuotationSubmit
                          ? (quotation, refItem, item, action) =>
                              props.onPackagingQuotationSubmit!(plan, quotation, refItem, item, action)
                          : undefined
                      }
                      onVehicleQuotationSubmit={
                        props.onVehicleQuotationSubmit
                          ? (quotation, refItem, item, action) =>
                              props.onVehicleQuotationSubmit!(plan, quotation, refItem, item, action)
                          : undefined
                      }
                      onTreatmentQuotationSubmit={
                        props.onTreatmentQuotationSubmit
                          ? (quotation, refItem, item, action) =>
                              props.onTreatmentQuotationSubmit!(plan, quotation, refItem, item, action)
                          : undefined
                      }
                      onDestinationQuotationSubmit={
                        props.onDestinationQuotationSubmit
                          ? (quotation, refItem, item, action) =>
                              props.onDestinationQuotationSubmit!(plan, quotation, refItem, item, action)
                          : undefined
                      }
                    />
                  </Stack>
                </Stack>
              );

              return (
                <ResiduePlanFlipCard
                  key={plan.id || plan.idPropostaResiduoPlano || index}
                  background={planBackground}
                  borderColor={planBorderColor}
                  header={planHeader}
                  actions={planActions}
                  frontContent={detailsContent}
                  backContent={summaryContent}
                />
              );
            })}
          </Stack>
        </Stack>
      </Paper>

      {props.referenceData && currentSelectedEquipmentPlan && (
        <ResiduePlanEquipmentModal
          opened={equipmentModalOpened}
          onClose={() => {
            setEquipmentModalOpened(false);
            setSelectedEquipmentPlan(null);
            setSelectedEquipmentItem(null);
          }}
          modoEdicao={!!props.isEdit}
          requireSupplier={props.requireSupplier}
          referenceData={props.referenceData}
          plan={currentSelectedEquipmentPlan}
          origItem={selectedEquipmentItem}
          item={selectedEquipmentItem}
          idPropostaResiduoPlano={currentSelectedEquipmentPlan.idPropostaResiduoPlano}
          zIndex={props.zIndex || 1000}
          onAddQuotation={
            props.onAddQuotation
              ? (plan) => {
                  props.onAddQuotation?.(plan);
                }
              : undefined
          }
          onSetPrimaryQuotation={
            props.onSetPrimaryQuotation
              ? (plan, quotation) => {
                  props.onSetPrimaryQuotation?.(plan, quotation);
                }
              : undefined
          }
          onDeleteQuotation={
            props.onDeleteQuotation
              ? (plan, quotation) => {
                  props.onDeleteQuotation?.(plan, quotation);
                }
              : undefined
          }
          callback={(item, action, confirmed) => {
            if (!confirmed || !currentSelectedEquipmentPlan || !props.onEquipmentSubmit) {
              return null;
            }

            props.onEquipmentSubmit(currentSelectedEquipmentPlan, item, action);
            return item;
          }}
          onQuotationSubmit={(quotation, refItem, item, action) => {
            if (!props.onEquipmentQuotationSubmit || !currentSelectedEquipmentPlan) {
              return;
            }

            props.onEquipmentQuotationSubmit(currentSelectedEquipmentPlan, quotation, refItem, item, action);
          }}
        />
      )}
      {props.referenceData && props.services && currentSelectedPackagingPlan && (
        <ResiduePlanPackagingModal
          opened={packagingModalOpened}
          onClose={() => {
            setPackagingModalOpened(false);
            setSelectedPackagingPlan(null);
            setSelectedPackagingItem(null);
          }}
          modoEdicao={!!props.isEdit}
          requireSupplier={props.requireSupplier}
          referenceData={props.referenceData}
          services={props.services}
          idEntidadeEndereco={proposalResidueData?.idEntidadeEndereco ?? props.data.idEntidadeEndereco}
          plan={currentSelectedPackagingPlan}
          origItem={selectedPackagingItem}
          item={selectedPackagingItem}
          idPropostaResiduoPlano={currentSelectedPackagingPlan.idPropostaResiduoPlano}
          zIndex={props.zIndex || 1000}
          onAddQuotation={
            props.onAddQuotation
              ? (plan) => {
                  props.onAddQuotation?.(plan);
                }
              : undefined
          }
          onSetPrimaryQuotation={
            props.onSetPrimaryQuotation
              ? (plan, quotation) => {
                  props.onSetPrimaryQuotation?.(plan, quotation);
                }
              : undefined
          }
          onDeleteQuotation={
            props.onDeleteQuotation
              ? (plan, quotation) => {
                  props.onDeleteQuotation?.(plan, quotation);
                }
              : undefined
          }
          callback={(item, action, confirmed) => {
            if (!confirmed || !currentSelectedPackagingPlan || !props.onPackagingSubmit) {
              return null;
            }
            props.onPackagingSubmit(currentSelectedPackagingPlan, item, action);
            return item;
          }}
          onQuotationSubmit={(quotation, refItem, item, action) => {
            if (!props.onPackagingQuotationSubmit || !currentSelectedPackagingPlan) {
              return;
            }

            props.onPackagingQuotationSubmit(currentSelectedPackagingPlan, quotation, refItem, item, action);
          }}
        />
      )}
      {props.referenceData && currentSelectedVehiclePlan && (
        <ResiduePlanVehicleModal
          opened={vehicleModalOpened}
          onClose={() => {
            setVehicleModalOpened(false);
            setSelectedVehiclePlan(null);
            setSelectedVehicleItem(null);
          }}
          modoEdicao={!!props.isEdit}
          requireSupplier={props.requireSupplier}
          referenceData={props.referenceData}
          plan={currentSelectedVehiclePlan}
          origItem={selectedVehicleItem}
          item={selectedVehicleItem}
          idPropostaResiduoPlano={currentSelectedVehiclePlan.idPropostaResiduoPlano}
          zIndex={props.zIndex || 1000}
          onAddQuotation={
            props.onAddQuotation
              ? (plan) => {
                  props.onAddQuotation?.(plan);
                }
              : undefined
          }
          onSetPrimaryQuotation={
            props.onSetPrimaryQuotation
              ? (plan, quotation) => {
                  props.onSetPrimaryQuotation?.(plan, quotation);
                }
              : undefined
          }
          onDeleteQuotation={
            props.onDeleteQuotation
              ? (plan, quotation) => {
                  props.onDeleteQuotation?.(plan, quotation);
                }
              : undefined
          }
          callback={(item, action, confirmed) => {
            if (!confirmed || !currentSelectedVehiclePlan || !props.onVehicleSubmit) {
              return null;
            }
            props.onVehicleSubmit(currentSelectedVehiclePlan, item, action);
            return item;
          }}
          onQuotationSubmit={(quotation, refItem, item, action) => {
            if (!props.onVehicleQuotationSubmit || !currentSelectedVehiclePlan) {
              return;
            }

            props.onVehicleQuotationSubmit(currentSelectedVehiclePlan, quotation, refItem, item, action);
          }}
        />
      )}
      {props.referenceData && currentSelectedTreatmentPlan && (
        <ResiduePlanTreatmentModal
          opened={treatmentModalOpened}
          onClose={() => {
            setTreatmentModalOpened(false);
            setSelectedTreatmentPlan(null);
            setSelectedTreatmentItem(null);
          }}
          modoEdicao={!!props.isEdit}
          requireSupplier={props.requireSupplier}
          referenceData={props.referenceData}
          plan={currentSelectedTreatmentPlan}
          origItem={selectedTreatmentItem}
          item={selectedTreatmentItem}
          idPropostaResiduoPlano={currentSelectedTreatmentPlan.idPropostaResiduoPlano}
          zIndex={props.zIndex || 1000}
          onAddQuotation={
            props.onAddQuotation
              ? (plan) => {
                  props.onAddQuotation?.(plan);
                }
              : undefined
          }
          onSetPrimaryQuotation={
            props.onSetPrimaryQuotation
              ? (plan, quotation) => {
                  props.onSetPrimaryQuotation?.(plan, quotation);
                }
              : undefined
          }
          onDeleteQuotation={
            props.onDeleteQuotation
              ? (plan, quotation) => {
                  props.onDeleteQuotation?.(plan, quotation);
                }
              : undefined
          }
          callback={(item, action, confirmed) => {
            if (!confirmed || !currentSelectedTreatmentPlan || !props.onTreatmentSubmit) {
              return null;
            }
            props.onTreatmentSubmit(currentSelectedTreatmentPlan, item, action);
            return item;
          }}
          onQuotationSubmit={(quotation, refItem, item, action) => {
            if (!props.onTreatmentQuotationSubmit || !currentSelectedTreatmentPlan) {
              return;
            }

            props.onTreatmentQuotationSubmit(currentSelectedTreatmentPlan, quotation, refItem, item, action);
          }}
        />
      )}
      {props.referenceData && currentSelectedDestinationPlan && (
        <ResiduePlanDestinationModal
          opened={destinationModalOpened}
          onClose={() => {
            setDestinationModalOpened(false);
            setSelectedDestinationPlan(null);
            setSelectedDestinationItem(null);
          }}
          modoEdicao={!!props.isEdit}
          requireSupplier={props.requireSupplier}
          referenceData={props.referenceData}
          plan={currentSelectedDestinationPlan}
          origItem={selectedDestinationItem}
          item={selectedDestinationItem}
          idPropostaResiduoPlano={currentSelectedDestinationPlan.idPropostaResiduoPlano}
          zIndex={props.zIndex || 1000}
          onAddQuotation={
            props.onAddQuotation
              ? (plan) => {
                  props.onAddQuotation?.(plan);
                }
              : undefined
          }
          onSetPrimaryQuotation={
            props.onSetPrimaryQuotation
              ? (plan, quotation) => {
                  props.onSetPrimaryQuotation?.(plan, quotation);
                }
              : undefined
          }
          onDeleteQuotation={
            props.onDeleteQuotation
              ? (plan, quotation) => {
                  props.onDeleteQuotation?.(plan, quotation);
                }
              : undefined
          }
          callback={(item, action, confirmed) => {
            if (!confirmed || !currentSelectedDestinationPlan || !props.onDestinationSubmit) {
              return null;
            }
            props.onDestinationSubmit(currentSelectedDestinationPlan, item, action);
            return item;
          }}
          onQuotationSubmit={(quotation, refItem, item, action) => {
            if (!props.onDestinationQuotationSubmit || !currentSelectedDestinationPlan) {
              return;
            }

            props.onDestinationQuotationSubmit(
              currentSelectedDestinationPlan,
              quotation,
              refItem,
              item,
              action
            );
          }}
        />
      )}
      {/* <Modal
        opened={planTypeModalOpened}
        onClose={() => setPlanTypeModalOpened(false)}
        title="Adicionar plano"
        size="sm"
        centered
        zIndex={(props.zIndex || 1000) + 10}
      >
        <Stack gap="xs">
          <Text size="sm">Escolha o tipo de plano que deseja adicionar.</Text>
          <Button
            variant="light"
            justify="flex-start"
            onClick={() => {
              setPlanTypeModalOpened(false);
              props.onAddPlan?.(ProposalResiduePlanCodeEnum.Mobilization);
            }}
          >
            Plano Mobilização
          </Button>
          <Button
            variant="light"
            justify="flex-start"
            onClick={() => {
              setPlanTypeModalOpened(false);
              props.onAddPlan?.(ProposalResiduePlanCodeEnum.Regular);
            }}
            >
              Plano Regular
            </Button>
          </Stack>
        </Modal> */}
    </>
  );
}
